import React from 'react';
import { format } from 'date-fns';

import { SuccessAlert, WarningAlertYellow } from 'components/ui/Alert';
import { useRunPayrollDataContext } from '../contexts/RunPayrollDataContext';
import { MessageType, MessageTypes, useMessageContext } from '../contexts/RunPayrollMessageContext';
import { LinkButtonV2 } from 'components/ui/Button';
import PFWarning from '../PFWarning';
import { shouldShowPfWarning } from '../utils/utils';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { useSelector } from 'react-redux';
import { useIsTdsFilingConfigRequired } from '../hooks/useIsTdsFilingConfigRequired';
import { TDSFilingWarning } from './TDSFilingWarning';
import { useIsPayPtEnabled } from '../ComplianceActions/hooks/useIsPayPtEnabled';

const MessageDisplay = () => {
  const data = useRunPayrollDataContext();
  const appConfig = useSelector(loggedInUserSelectors.appConfig);
  const isTdsFilingConfigRequired = useIsTdsFilingConfigRequired();

  const isRXIntegrated = useSelector(loggedInUserSelectors.currentOrganization)?.isRXIntegrated;
  const isPayPtOnRunPayrollEnabled = useIsPayPtEnabled();
  const enablePfEcRBannerOnRunPayroll = Boolean(appConfig.enablePfEcRBannerOnRunPayroll);

  const messageContext = useMessageContext();
  const message = messageContext?.message;

  if (!data && !message) {
    return null;
  }
  const isPayrollExecuted = (data?.numPaid || 0) > 0;
  const payrollMonth = data?.payrollMonth;
  const isPfWarningVisible = payrollMonth
    ? shouldShowPfWarning(payrollMonth) && isPayrollExecuted && enablePfEcRBannerOnRunPayroll
    : false;

  const getMessage = (message: MessageType) => {
    switch (message.type) {
      case MessageTypes.FINALIZE_IN_PROGRESS: {
        return (
          <WarningAlertYellow className="mb-5">
            Your Payroll is being finalized and will be completed in a few minutes
          </WarningAlertYellow>
        );
      }
      case MessageTypes.FINALIZED:
        const payrollMonth: string = message.data;
        const payrollMonthDate = new Date(payrollMonth + 'T00:00:00');
        return (
          <SuccessAlert className="mb-5">
            Payroll finalized for {format(payrollMonthDate, 'MMMM yyyy')}. You can verify the salary
            register{' '}
            <a
              target="_blank"
              href={`/reports/salaryRegister?pd=${format(payrollMonthDate, 'dd-MM-yy')}`}
              rel="noreferrer">
              <LinkButtonV2>here</LinkButtonV2>
            </a>
            .
          </SuccessAlert>
        );

      case MessageTypes.EXECUTION_REQUESTED:
        return (
          <SuccessAlert className="mb-5">
            Your request has been received. Please do not change any payment settings for next 30
            minutes.
          </SuccessAlert>
        );

      // Only reachable from the Phase 2 resume flow (ResumeExecution), so no
      // feature-flag check is needed here — a legacy org never emits it.
      case MessageTypes.EXECUTION_RESUMED:
        return (
          <SuccessAlert className="mb-5">
            Payroll is running again, picking up from where it stopped. Employees already paid this
            month will not be paid again. Salary transactions will be processed within 24 hours.
          </SuccessAlert>
        );
    }
    return null;
  };

  if (isPayPtOnRunPayrollEnabled) {
    return null;
  }

  return (
    <div>
      {isTdsFilingConfigRequired && !!data?.isPayrollFinalized && (
        <TDSFilingWarning payrollMonth={payrollMonth} />
      )}

      {isPfWarningVisible ? <PFWarning className="mb-5" payrollMonth={payrollMonth!} /> : null}

      {/*
        A run that stopped for a reason only support can clear.
        
        Kept short on purpose. Anything that hints at what went wrong, or at
        money having moved, reads as "did you pay everyone twice?" on a payroll
        screen — the one thing an admin here is afraid of. They cannot act on
        the detail either way, so the notice says only that we know and who to
        ask. The specifics live in the run's failure_reason for support.
      */}
      {data?.isExecutionStuck ? (
        <WarningAlertYellow className="mb-5">
          Something went wrong while processing this payroll. Please reach out to support.
        </WarningAlertYellow>
      ) : null}

      {message ? (
        getMessage(message)
      ) : data?.isExecutionRequested && !data?.isExecutionStuck ? (
        /*
          Suppressed when the run is stuck: isExecutionRequested stays true for a
          run that has stopped, so this would sit under the warning promising
          salaries within 24 hours that are not coming. One or the other, never
          both.
        */
        <SuccessAlert className="mb-5">
          {isRXIntegrated
            ? 'You have requested execution of this payroll. Your request will be completed within 12 hours.'
            : 'Your payroll execution request has been received. Salary transactions will be processed within 24 hours.'}
        </SuccessAlert>
      ) : null}
    </div>
  );
};

export default MessageDisplay;
